import { enqueueMutation, OfflineQueuedError } from "@/lib/offlineQueue";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API ${status}`);
    this.name = "ApiError";
  }
}

export { OfflineQueuedError };

const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError;
}

export async function api<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const method = (options?.method ?? "GET").toUpperCase();
  const isMutation = MUTATION_METHODS.has(method);

  try {
    const res = await fetch(path, {
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
      ...options,
    });

    if (res.status === 401) {
      window.location.href = "/login";
      throw new ApiError(401, null);
    }

    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(res.status, body);
    }

    return res.json() as Promise<T>;
  } catch (err) {
    // Only offline-queue mutations that failed because the browser has
    // no connectivity. Server-rejected requests (ApiError with a status)
    // surface normally.
    if (
      isMutation &&
      isNetworkError(err) &&
      typeof navigator !== "undefined" &&
      navigator.onLine === false
    ) {
      const body =
        typeof options?.body === "string"
          ? options.body
          : options?.body != null
            ? String(options.body)
            : null;
      const id = await enqueueMutation({ method, url: path, body }).catch(
        () => -1,
      );
      if (id >= 0) {
        throw new OfflineQueuedError(id);
      }
    }
    throw err;
  }
}
